import {lazy, useEffect, Suspense} from "react"
import {useDispatch} from "react-redux"
import {createBrowserRouter, RouterProvider} from "react-router-dom"
import ErrorBoundary from "./ErrorBoundary/index"
import Toaster from "./components/Toaster"
import Landing from "./layout/Landing"
import {setCredentials} from "./pages/Login/authSlice"
import RequiredAuth from "./routes/RequiredAuth"
import {handleEnableModulePermissions} from "./constants/modulePermissions"
import userInfo from "./pages/Common/userInfo"
import {handleMenuList, MenuList} from "./constants/menuItems"
import {setMenuList} from "./pages/Common/modulesSlice"
import {rolesPermissions} from "./constants/permissions"
import Loader from "./components/Loader"
import Box from "@mui/material/Box"
// import {init as initApm} from "@elastic/apm-rum"

function App() {
    const CompanyManagement = lazy(() => import("./pages/CompanyManagement"))
    const Profile = lazy(() => import("./pages/CompanyManagement/features/Profile"))
    const Network = lazy(() => import("./pages/NetworkDetails"))
    const Admin = lazy(() => import("./pages/CompanyManagement/features/Admin"))
    const Registration = lazy(() => import("./pages/CompanyManagement/features/Registration"))
    const RegistrationDetails = lazy(
        () => import("./pages/CompanyManagement/features/Registration/Features/RegistrationDetails")
    )
    const Mapping = lazy(() => import("./pages/CompanyManagement/features/Mapping"))
    const Settlements = lazy(() => import("./pages/Settlements"))
    const PayoutInfo = lazy(() => import("./pages/Settlements/Features/PayoutInfo"))
    const AbnormalTransaction = lazy(() => import("./pages/Settlements/Features/AbnormalTransaction"))
    const DisputeTransaction = lazy(() => import("./pages/Settlements/Features/DisputeTransaction"))
    const Locations = lazy(() => import("./pages/Locations"))
    const Stations = lazy(() => import("./pages/Locations/Features/Stations"))
    const Chargers = lazy(() => import("./pages/Locations/Features/Chargers"))
    const ChargersProfile = lazy(() => import("./pages/Locations/Features/Chargers/Features/ChargersProfile"))
    const BulkMapping = lazy(() => import("./pages/Locations/Features/Chargers/Features/BulkMapping"))
    const Tariff = lazy(() => import("./pages/Tariff"))
    const Tokens = lazy(() => import("./pages/Tokens"))
    const Session = lazy(() => import("./pages/Sessions"))
    const SessionDetails = lazy(() => import("./pages/Sessions/Features/SessionDetails"))
    const SessionTimeline = lazy(() => import("./pages/Sessions/Features/SessionTimeline"))
    const Logs = lazy(() => import("./pages/Logs"))
    const UserProfile = lazy(() => import("./pages/UserProfile"))
    const Complaints = lazy(() => import("./pages/Complaints"))
    const Configurations = lazy(() => import("./pages/Configurations"))
    const CompanyLevelSettlementType = lazy(() => import("./pages/Configurations/Features/CompanyLevelSettlementType"))
    const EditCompanyLevelSettlementType = lazy(
        () => import("./pages/Configurations/Features/EditCompanyLevelSettlementType")
    )
    const TariffLevelSettlementType = lazy(() => import("./pages/Configurations/Features/TariffLevelSettlementType"))
    const EditCompanyLevelTariffType = lazy(
        () => import("./pages/Configurations/Features/EditTariffLevelSettlementType")
    )
    const CompanyLevelRateLimitType = lazy(() => import("./pages/Configurations/Features/CompanyLevelRateLimitType"))
    const EditRateLimitType = lazy(() => import("./pages/Configurations/Features/EditRateLimitType"))

    // const apm = initApm({
    //     // Set required service name (allowed characters: a-z, A-Z, 0-9, -, _, and space)
    //     serviceName: "hub-ui",
    //     // Set custom APM Server URL (default: http://localhost:8200)
    //     serverUrl: process.env.REACT_APP_APM_KEY,
    //     // Set the service version (required for source map feature)
    //     serviceVersion: "",
    //     // Set the service environment
    //     environment: process.env.REACT_APP_APM !== "local" ? process.env.REACT_APP_APM : "local",
    //     active: process.env.REACT_APP_APM === "local" || process.env.REACT_APP_APM === "production" ? false : true,
    //     disableInstrumentations: ["page-load", "history", "eventtarget", "click"],
    // })

    const dispatch = useDispatch()

    const userPermissions = userInfo("permissions")

    useEffect(() => {
        dispatch(setCredentials({token: ""}))
    }, [])

    useEffect(() => {
        userPermissions.length > 0 && handleEnableModulePermissions()
        const currentPermissions =
            userPermissions.length > 0 ? userPermissions.filter((item: any) => rolesPermissions.includes(item)) : []
        const currentMenuPermissions = handleMenuList(MenuList, currentPermissions)
        dispatch(setMenuList({menuList: currentMenuPermissions ?? []}))
        userPermissions.length > 0 && handleMenuList(MenuList, userPermissions)
    }, [userPermissions])

    const router = createBrowserRouter([
        {
            element: <Landing />,
            errorElement: <ErrorBoundary />,
            path: "/auth",
        },

        {
            element: <RequiredAuth />,
            errorElement: <ErrorBoundary />,
            children: [
                {
                    path: "company-management",
                    errorElement: <ErrorBoundary />,
                    element: <CompanyManagement />,
                    children: [
                        {
                            path: "profile",
                            errorElement: <ErrorBoundary />,
                            element: <Profile />,
                        },
                        {
                            path: "admin",
                            errorElement: <ErrorBoundary />,
                            element: <Admin />,
                        },
                        {
                            path: "registration",
                            errorElement: <ErrorBoundary />,
                            element: <Registration />,
                        },
                        {
                            path: "registration/details",
                            errorElement: <ErrorBoundary />,
                            element: <RegistrationDetails />,
                        },
                        {
                            path: "mapping",
                            errorElement: <ErrorBoundary />,
                            element: <Mapping />,
                        },
                    ],
                },
                {
                    path: "settlement",
                    errorElement: <ErrorBoundary />,
                    element: <Settlements />,
                    children: [
                        {
                            path: "payout-info",
                            errorElement: <ErrorBoundary />,
                            element: <PayoutInfo />,
                        },
                        {
                            path: "abnormal-transaction",
                            errorElement: <ErrorBoundary />,
                            element: <AbnormalTransaction />,
                        },
                        {
                            path: "dispute-transaction",
                            errorElement: <ErrorBoundary />,
                            element: <DisputeTransaction />,
                        },
                    ],
                },
                {
                    path: "/",
                    errorElement: <ErrorBoundary />,
                    element: <Profile />,
                },
                {
                    path: "network",
                    errorElement: <ErrorBoundary />,
                    element: <Network />,
                },
                {
                    path: "locations",
                    errorElement: <ErrorBoundary />,
                    element: <Locations />,
                    children: [
                        {
                            path: "stations",
                            errorElement: <ErrorBoundary />,
                            element: <Stations />,
                        },
                        {
                            path: "chargers",
                            errorElement: <ErrorBoundary />,
                            element: <Chargers />,
                        },
                        {
                            path: "chargers/charger-profile",
                            errorElement: <ErrorBoundary />,
                            element: <ChargersProfile />,
                        },
                        {
                            path: "chargers/bulk-mapping",
                            errorElement: <ErrorBoundary />,
                            element: <BulkMapping />,
                        },
                    ],
                },
                {
                    path: "tariff",
                    errorElement: <ErrorBoundary />,
                    element: <Tariff />,
                },
                {
                    path: "tokens",
                    errorElement: <ErrorBoundary />,
                    element: <Tokens />,
                },
                {
                    path: "sessions",
                    errorElement: <ErrorBoundary />,
                    element: <Session />,
                    children: [
                        {
                            path: "session-details",
                            errorElement: <ErrorBoundary />,
                            element: <SessionDetails />,
                        },
                        {
                            path: "session-timeline",
                            errorElement: <ErrorBoundary />,
                            element: <SessionTimeline />,
                        },
                    ],
                },
                {
                    path: "logs",
                    errorElement: <ErrorBoundary />,
                    element: <Logs />,
                },
                {
                    path: "user-profile",
                    element: <UserProfile />,
                },
                {
                    path: "complaints",
                    errorElement: <ErrorBoundary />,
                    element: <Complaints />,
                },
                {
                    path: "configurations",
                    errorElement: <ErrorBoundary />,
                    element: <Configurations />,
                },
                {
                    path: "configurations/company-level-settlement-type",
                    errorElement: <ErrorBoundary />,
                    element: <CompanyLevelSettlementType />,
                },
                {
                    path: "configurations/edit-company-level-settlement-type",
                    errorElement: <ErrorBoundary />,
                    element: <EditCompanyLevelSettlementType />,
                },
                {
                    path: "configurations/company-level-tariff-type",
                    errorElement: <ErrorBoundary />,
                    element: <TariffLevelSettlementType />,
                },
                {
                    path: "configurations/edit-company-level-tariff-type",
                    errorElement: <ErrorBoundary />,
                    element: <EditCompanyLevelTariffType />,
                },
                {
                    path: "configurations/company-level-rate-limit-type",
                    errorElement: <ErrorBoundary />,
                    element: <CompanyLevelRateLimitType />,
                },
                {
                    path: "configurations/edit-company-level-rate-limit-type",
                    errorElement: <ErrorBoundary />,
                    element: <EditRateLimitType />,
                },
            ],
        },
        // {
        //     path: "*",
        //     element: <ErrorBoundary />,
        //     errorElement: <ErrorBoundary />,
        // },
    ])

    return (
        <Suspense
            fallback={
                <Box
                    sx={{
                        width: "100%",
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Loader />
                </Box>
            }
        >
            <Toaster vertical={"top"} horizontal={"center"} autoHideDuration={3000} />
            <RouterProvider router={router} />
        </Suspense>
    )
}

export default App
